import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  PushNotification,
  PushNotificationActionPerformed,
  PushNotifications,
  PushNotificationToken
} from '@capacitor/push-notifications';
import { Store } from '@ngrx/store';
import { loadNotifications } from 'app/state/account/account.actions';
import { selectUser } from 'app/state/user/user.select';
import { distinctUntilChanged } from 'rxjs/operators';
import { AlertService } from './alert.service';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root'
})
export class PushService {

  private currentToken: string | null = null;
  private initialized = false;

  constructor(
    private httpService: HttpService,
    private alertService: AlertService,
    private store: Store,
    private router: Router
  ) {
    this.store.select(selectUser).pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id)
    ).subscribe((user) => {
      if (!user || !user.account || Capacitor.getPlatform() === 'web') {
        return;
      }

      if (!this.initialized) {
        this.initialized = true;
        this.initPush();
        return;
      }

      PushNotifications.register();
    });
  }

  initPush() {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    PushNotifications.requestPermissions().then(result => {
      if (result.receive === 'granted') {
        PushNotifications.register();
      } else {
        console.warn('Push notifications permission not granted.');
      }
    });

    PushNotifications.addListener('registration', (token: PushNotificationToken) => {
      this.currentToken = token.value;
      this.registerTokenOnBackend(token.value);
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Push registration error:', error);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotification) => {
      const title = notification.title || 'Notificação';
      const body = notification.body || '';
      this.alertService.notification(title, body);
      this.store.dispatch(loadNotifications());
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action: PushNotificationActionPerformed) => {
      this.redirectTo(action.notification);
    });
  }

  unregisterToken(): Promise<void> {
    if (!this.currentToken) {
      return Promise.resolve();
    }

    const token = this.currentToken;
    this.currentToken = null;

    return new Promise<void>((resolve) => {
      this.httpService.post('/v1/clients/devices/unregister', { token }).subscribe({
        next: () => resolve(),
        error: (err) => {
          console.error('Failed to unregister FCM token on backend:', err);
          resolve();
        }
      });
    });
  }

  private registerTokenOnBackend(token: string) {
    this.httpService.post('/v1/clients/devices', {
      token,
      platform: Capacitor.getPlatform(),
      version: ''
    }).subscribe({
      error: (err) => console.error('Failed to register FCM token on backend:', err)
    });
  }

  private redirectTo(notification: PushNotification) {
    const data = notification.data || {};

    if (data.useRouter) {
      this.router.navigate([`${data.useRouter}`]);
      return;
    }

    if (data.link) {
      window.location.href = data.link;
    }
  }
}
